/**
 * Shared axios instance + request helper for TanStack Query hooks in
 * src/queries/*. Replaces per-file `${env.API_URL}/v1` string-building.
 *
 * env.API_URL is only populated once `loadConfig()` resolves in src/index.js's
 * bootstrap(), *before* ReactDOM renders — so it's always ready by the time any
 * query/mutation hook actually runs. Read it per-request via an interceptor
 * (not baked into `baseURL` at module-load time) so this stays correct even if
 * a query hook module is ever imported earlier than expected.
 *
 * Unlike hook/services.js's CallService (which always resolves `{response}` or
 * `{error}`, never throws), `request()` throws on an `{error}` envelope or a
 * non-2xx response — that's what makes useMutation's onError/isError work.
 *
 * Auth token attachment lives here, not in every call site: most of the app
 * calls `apiClient.get/post(...)` directly rather than through `request()`
 * below, so a per-call `token` option would silently miss almost every
 * request. The backend's `Authenticate` middleware only 401s on a bad/missing
 * token (business-logic failures like a wrong password are 400s via
 * ServiceHandler), so a global 401 handler below is safe to treat as "session
 * is dead" without risking false-triggering on an ordinary failed request.
 */
import axios from "axios";
import localforage from "localforage";
import env from "../config/env";
import { ReqMethods } from "../enums";

const apiClient = axios.create();

apiClient.interceptors.request.use(async (config) => {
  config.baseURL = `${env.API_URL}/v1`;
  if (!config.headers?.["x-auth-token"]) {
    const token = await localforage.getItem("token");
    if (token) {
      config.headers = { ...config.headers, "x-auth-token": `Bearer ${token}` };
    }
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !window.location.pathname.startsWith("/auth/sign-in")) {
      await localforage.clear();
      window.location.replace("/auth/sign-in");
    }
    return Promise.reject(error);
  },
);

export function request(
  method,
  path,
  { query, payload, token, apiKey, timeout } = {},
) {
  const url = query ? `${path}?${query}` : path;
  const headers = {};
  if (typeof token === "string" && token) headers["x-auth-token"] = `Bearer ${token}`;
  if (apiKey) headers.apiKey = apiKey;

  return apiClient
    .request({
      method,
      url,
      data: payload,
      headers,
      // Optional per-call override (e.g. a slow provider-channels lookup or a
      // long-running task run) — omitted entirely so existing callers keep
      // axios's default timeout.
      ...(timeout ? { timeout } : {}),
    })
    .then((res) => res.data);
}

export { ReqMethods };
export default apiClient;
